import React from 'react';
import PropTypes from 'prop-types';
import api from '@tutorbot/directory-api';
import { scroller } from 'react-scroll';
import { Modal } from 'antd';
import LazyLoad from 'react-lazyload';
import Button from '../../Button';
import { LocalizationContext } from '../../../hoc/Localization';
import { EditorContext } from '../../../hoc/Editor';
import { UserContext } from '../../../hoc/User';
import { SettingsContext } from '../../../hoc/Settings';
import { SecurityContext } from '../../../hoc/Security';
import SchoolLink from '../../TinyComponents/SchoolLink';
import IconText from '../../TinyComponents/IconText';
import InlineEditor from '../../InlineEditor';
import Rating from '../../Rating';
import usePriceFormat from '../../../utils/priceFormat';
import ImageUpload from '../../InlineEditor/ImageUpload';
import ScheduleMoment from '../ScheduleMoment';
import Picture from '../../Picture';
import LinkWrapper from '../../Card/LinkWrapper';
import { toggleFavourite } from '../../../utils/directory';
import styles from './styles.less';
import { trainingTypeOptions } from '../../../config/trainingTypeOptions';
import Price from '../Price'

const Header = ({
  type,
  course,
  canEdit,
  schedules,
  setRegister,
  selectedSchedule,
  setSelectedSchedule,
  showMonthlyPrice,
  search,
}) => {
  const { t, locale, translate } = React.useContext(LocalizationContext);
  const { edited, updateContent, editMode } = React.useContext(EditorContext);
  const { user, refreshUser } = React.useContext(UserContext);
  const {
    domainSettings: { featureSelfLearning },
  } = React.useContext(SettingsContext);
  const { favourite: favouriteClicked, setFavourite } = React.useContext(SecurityContext);

  const [cancellationModal, setCancellationModal] = React.useState(false);
  const [fetchedCourseTypes, setFetchedCourseTypes] = React.useState([]);
  const [fetchedCategories, setFetchedCategories] = React.useState([]);
  const [fetchedLanguages, setFetchedLanguages] = React.useState([]);
  const [fetchedLevels, setFetchedLevels] = React.useState([]);
  const [fetchedSkills, setFetchedSkills] = React.useState([]);
  const [fetchedPerks, setFetchedPerks] = React.useState([]);
  const [showRegister, setShowRegister] = React.useState(false);
  const [isFavourite, setIsFavourite] = React.useState(false);
  const [favourites, setFavourites] = React.useState(course.favouriteNumber);
  const [loadingFavourite, setLoadingFavourite] = React.useState(false);
  const [smallScreen, setSmallScreen] = React.useState(false);

  const courseEndDate = Date.parse(`${course.end_date}`);
  const dateNow = Date.now();
  React.useEffect(() => {
    if (course.end_date !== null && courseEndDate < dateNow) {
      setRegister(false);
      setShowRegister(true);
    } else {
      setRegister(true);
      setShowRegister(false);
    }
  }, []);

  React.useEffect(() => {
    const windowWidth = window.screen.width;
    if (windowWidth < 768) {
      setSmallScreen(true);
    }
  }, []);

  React.useEffect(() => {
    if (canEdit) {
      if (!fetchedCourseTypes.length) {
        (async () => {
          setFetchedCourseTypes(await api().courseTypes.all());
        })();
      }

      if (!fetchedLanguages.length) {
        (async () => {
          setFetchedLanguages(await api().languages.all());
        })();
      }

      if (!fetchedLevels.length) {
        (async () => {
          setFetchedLevels(await api().levels.all());
        })();
      }

      if (!fetchedCategories.length) {
        (async () => {
          setFetchedCategories(await api().categories.all());
        })();
      }

      if (!fetchedSkills.length) {
        (async () => {
          setFetchedSkills(await api().skills.all());
        })();
      }

      if (!fetchedPerks.length) {
        (async () => {
          setFetchedPerks(await api().perks.all());
        })();
      }
    }
  }, []);

  React.useEffect(() => {
    // eslint-disable-next-line react/prop-types
    if (user && user.favourites) {
      // eslint-disable-next-line react/prop-types
      const favourite = user.favourites.find(f => f.course_id === course.shortId);
      if (favourite && !isFavourite) {
        setIsFavourite(true);
      } else {
        setIsFavourite(false);
      }
    }
  }, [user]);

  React.useEffect(() => {
    if (favouriteClicked && user) {
      setFavourite(false);
      // eslint-disable-next-line no-use-before-define
      handleFavouriteClick();
    }
  }, [favouriteClicked, user]);

  async function handleFavouriteClick() {
    if (user && !loadingFavourite) {
      setLoadingFavourite(true);
      await toggleFavourite(course.shortId, isFavourite, 'course');
      await refreshUser();
      if (isFavourite) {
        setFavourites(Number(favourites) - 1);
      } else {
        setFavourites((Number(favourites) || 0) + 1);
      }
      setLoadingFavourite(false);
    }
    if (!user) {
      setFavourite(true);
    }
  }

  const {
    age,
    title,
    image,
    Group,
    level,
    perks,
    price,
    rating,
    school,
    skills,
    period,
    period_value: periodValue,
    language,
    location,
    currency,
    discount,
    category,
    trainingType,
    coursetypes,
    age_min: ageMin,
    end_date: endDate,
    free_course: freeCourse,
    discounted_price: discountedPrice,
    monthly_price: monthlyPrice,
    'Max Group Size': maxGroupSize,
    'Min Group Size': minGroupSize,
    last_minute_start: lastMinuteStart,
    last_minute_end: lastMinuteEnd,
    last_minute_discount: lastMinuteDiscount,
    early_bird_start: earlyBirdStart,
    early_bird_end: earlyBirdEnd,
    early_bird_discount: earlyBirdDiscount,
    discount_start: discountStart,
    discount_end: discountEnd,
    requestUrl,
    self_learning: selfLearning,
    online,
  } = course;

  const symbolCurrency = {
    AMD: '֏',
    USD: '$',
    EUR: '€',
    RUB: '₽',
  };

  let courseLanguage = null;

  if (coursetypes) {
    courseLanguage = translate(coursetypes, 'title');
  }

  if (courseLanguage && language && Object.keys(language).length) {
    courseLanguage += ` (${translate(language, 'Name')})`;
  } else if (language && Object.keys(language).length) {
    courseLanguage = translate(language, 'Name');
  }

  // eslint-disable-next-line
  let currentPeriod = '';
  if (period && period !== 'custom') {
    currentPeriod = t(`course.details.content.${period}`);
  }
  if (period && period === 'custom') {
    currentPeriod = periodValue;
  }
  const address = location && Object.keys(location).length ? [location.city, location.street] : [];
  if (location && location.house_number) {
    address.push(location.house_number);
  }
  address.push(location.zip_code);

  let groupSize = '';

  if (Group && maxGroupSize) {
    groupSize = `${maxGroupSize}`;

    if (minGroupSize) {
      groupSize = `${minGroupSize} - ${maxGroupSize}`;
    }
  }

  let showGroup;

  if (Group) {
    showGroup = `${t('details.edit.courses.form.group_yes')} ${groupSize}`;
  } else {
    showGroup = t('details.edit.courses.form.group_no');
  }

  return (
    <div
      id="courseHeader"
      className={!editMode ? `${styles.searchMod} ${styles[type]}` : styles.editMod}
    >
      {/* ----------Image---------- */}
      <div className={!editMode ? styles.imageContainer : styles.imageContainerEdit}>
        <LazyLoad offset={300} scroll>
          {!!canEdit && !!editMode && (
            <ImageUpload
              uploadHandler={url => {
                updateContent({ ...edited, image: url });
              }}
              text={t('course.edit.cover')}
              type="school"
              folder="course"
              className="editCourseImageButton"
              containerClassName="editCourseImage"
              withIcon
            />
          )}
          {search ? (
            <LinkWrapper data={course}>
              <Picture src={image} alt={title} />
            </LinkWrapper>
          ) : (
              <Picture src={image} alt={title} />
            )}
        </LazyLoad>
      </div>
      {/* ----------Info---------- */}
      <div className={!editMode ? styles.infoContainer : styles.infoContainerEdit}>
        {/* ----------Title and Rating---------- */}
        <div className={styles.header}>
          <div className={styles.shareInfo}>
            <div className={styles.flexJustified}>
              {/* ----------Rating---------- */}
              <div className={styles.flex}>
                {!editMode && (
                  <div className={styles.footerCardInfo}>
                    <Rating rating={rating} favourites={favourites || '0'} />
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* ----------Title---------- */}
          <div className={styles.title}>
            <div className={styles.title_schedule}>
              <div style={{ display: 'flex' }}>
                <h1
                  style={{ overflow: editMode ? 'unset' : 'hidden' }}
                  className={styles.titleNameCourse}
                >
                  <InlineEditor
                    value={title}
                    type="textarea"
                    label="title"
                    hasTranslations
                    settings={{
                      placeholder: t('course.edit.title.placeholder'),
                      className: styles.titleEditor,
                      rows: 2,
                    }}
                  >
                    {search ? (
                      <LinkWrapper data={course}>
                        <span style={{ color: 'black' }}>
                          {course.title || course[`title_${locale}`]}
                        </span>
                      </LinkWrapper>
                    ) : (
                        <span>{course.title || course[`title_${locale}`]}</span>
                      )}
                  </InlineEditor>
                </h1>
                {!editMode && smallScreen && <ScheduleMoment style={styles} data={course} noTime />}
              </div>
              {/* ----------School info---------- */}
              <div className={styles.schoolLink}>
                {!!school && !editMode && !!Object.keys(school).length && (
                  <SchoolLink school={school} />
                )}
              </div>
              {!editMode && !smallScreen && <ScheduleMoment style={styles} data={course} noTime />}
            </div>
          </div>
        </div>

        {/* ----------Description---------- */}
        <div className={styles.descriptionRow}>
          <div className={styles.descriptionCol}>
            {user && user.isAdmin && (
              <InlineEditor
                label="end_date"
                type="date"
                value={endDate}
                settings={{
                  className: styles.endDate,
                  placeholder: t('course.edit.end_date.placeholder'),
                }}
              />
            )}
            {(trainingType || editMode) && (
              <div className={styles.trainingType}>
                <IconText
                  icon="/static/images/course/webinar.svg"
                  text={
                    <>
                      <InlineEditor
                        label="trainingType"
                        type="select"
                        value={trainingType}
                        options={trainingTypeOptions}
                        selectKey="title"
                        settings={{
                          style: {
                            width: '35%',
                          },
                          placeholder: t('course.trainType.placeholder'),
                          data_gh: 'course_type_editor',
                        }}
                      >
                        {!!trainingType && (
                          <div className={styles.category}>{trainingType[`title_${locale}`]}</div>
                        )}
                      </InlineEditor>
                    </>
                  }
                />
              </div>
            )}
            {editMode && (
              <div className={styles.editCategories}>
                <InlineEditor
                  label="category"
                  type="select"
                  value={category}
                  options={fetchedCategories}
                  selectKey="title"
                  settings={{
                    style: {
                      width: '35%',
                      marginBottom: '5px',
                    },
                    placeholder: t('course.edit.category.placeholder'),
                  }}
                />
                <InlineEditor
                  label="coursetypes"
                  type="select"
                  value={coursetypes}
                  options={
                    category &&
                    fetchedCourseTypes.filter(ct => ct.category && ct.category === category.id)
                  }
                  selectKey="title"
                  settings={{
                    style: {
                      width: '35%',
                      marginBottom: '5px',
                    },
                    className: [styles.editor, styles.coursetype].join(' '),
                    placeholder: t('course.edit.coursetype.placeholder'),
                  }}
                >
                  {courseLanguage}
                </InlineEditor>
                <InlineEditor
                  label="language"
                  type="select"
                  value={language}
                  options={category && fetchedLanguages.filter(ln => ln.category === category.id)}
                  selectKey="Name"
                  settings={{
                    style: {
                      width: '35%',
                      marginBottom: '5px',
                    },
                    className: styles.editor,
                    placeholder: t('course.edit.language.placeholder'),
                  }}
                />
              </div>
            )}
            {((!!address && !!address.length) || editMode) && (
              <>
                <div className={styles.location}>
                  <IconText
                    icon="/static/images/course/location.svg"
                    text={
                      <InlineEditor label="location" type="location" value={location}>
                        <span className={styles.wrapOneLine}>{address.join(' ')}</span>
                      </InlineEditor>
                    }
                  />
                </div>
                {editMode && (
                  <>
                    <IconText
                      icon="/static/images/course/skills.png"
                      text={
                        <InlineEditor
                          label="skills"
                          type="multiple"
                          value={skills || []}
                          renderOption={option => option.title}
                          options={
                            category && fetchedSkills.filter(i => i.category === category.id)
                          }
                          selectKey="title"
                          settings={{
                            className: styles.editor,
                            placeholder: t('course.edit.skills.placeholder'),
                          }}
                        />
                      }
                    />
                    <IconText
                      icon="/static/images/course/perks.png"
                      text={
                        <InlineEditor
                          label="perks"
                          type="multiple"
                          value={perks || []}
                          options={fetchedPerks}
                          renderOption={option => option.title}
                          selectKey="title"
                          settings={{
                            className: styles.editor,
                            placeholder: t('course.edit.perks.placeholder'),
                          }}
                        />
                      }
                    />
                  </>
                )}
              </>
            )}
            {(!!Group && !!Group.length) || editMode ? (
              <IconText
                icon="/static/images/course/group.svg"
                text={
                  <InlineEditor
                    label="Group"
                    type="group"
                    value={Group}
                    settings={{
                      className: styles.group,
                    }}
                  >
                    {showGroup}
                  </InlineEditor>
                }
              />
            ) : null}

            {(!!level || editMode) && (
              <IconText
                icon="/static/images/course/level.svg"
                text={
                  <InlineEditor
                    label="level"
                    type="select"
                    value={level}
                    options={fetchedLevels}
                    selectKey="Name"
                    settings={{
                      className: styles.editor,
                      placeholder: t('course.edit.level.placeholder'),
                    }}
                  >
                    {!!level && translate(level, 'Name')}
                  </InlineEditor>
                }
              />
            )}

            {editMode && featureSelfLearning && (
              <IconText
                icon="/static/images/course/selfLearning.png"
                text={
                  <InlineEditor label="self_learning" type="boolean" value={selfLearning}>
                    {t('course.edit.selfLearning')}
                  </InlineEditor>
                }
              />
            )}

            {editMode && (
              <IconText
                icon="/static/images/course/live.svg"
                text={
                  <InlineEditor label="online" type="boolean" value={online}>
                    {t('course.edit.online')}
                  </InlineEditor>
                }
              />
            )}

            {(!!age || editMode) && (
              <IconText
                icon="/static/images/course/age.png"
                text={
                  <InlineEditor
                    label={['age_min', 'age']}
                    type="range"
                    value={[ageMin, age]}
                    settings={[
                      {
                        min: 0,
                        max: age || 100,
                        placeholder: t('course.edit.min_age.placeholder'),
                      },
                      {
                        min: ageMin || 0,
                        max: 100,
                        placeholder: t('course.edit.max_age.placeholder'),
                      },
                    ]}
                  >
                    {`${ageMin}-${age}`}
                  </InlineEditor>
                }
              />
            )}

            {/* {upcomingEvent && !editMode && (
              <IconText
                icon="/static/images/course/schedule.png"
                text={new Date(upcomingEvent.start_date).toLocaleDateString()}
              />
            )} */}
            {!!courseLanguage && !editMode && !search && (
              <div className={styles.categoryTypes}>
                <div className={styles.courseLang}>
                  <IconText icon="/static/images/course/courseLng.svg" text={`${courseLanguage}`} />
                </div>
                {/* ----------Category---------- */}
                <div className={styles.categoryContainer}>
                  <span>
                    {!!category && (
                      <div className={styles.category}>{translate(category, 'name')}</div>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
          {/* ----------Price and Register---------- */}

          <div className={search ? `${styles.priceCol} ${styles.priceSearch}` : styles.priceCol}>
            <InlineEditor label="price" type="price" value={price}>
              <Price course={course} showMonthlyPrice={showMonthlyPrice} isEditMode={editMode} />
            </InlineEditor>

            {type === 'header' && (
              <>
                {/* ----------Register---------- */}
                {!editMode && (
                  <Button
                    version="large"
                    type="primary"
                    id="register"
                    disabled={showRegister}
                    onClick={() => {
                      if (!requestUrl) {
                        if (schedules.length) {
                          if (!selectedSchedule && schedules) {
                            const index = course.schedules.length - 1;
                            setSelectedSchedule(course.schedules[index]);
                          }
                        } else {
                          setRegister(true);
                        }
                        setTimeout(() => {
                          scroller.scrollTo('register', {
                            duration: 500,
                            delay: 100,
                            smooth: true,
                            offset: -230,
                          });
                        });
                      }
                    }}
                  >
                    {requestUrl ? (
                      <a target="blank" href={`${requestUrl}`}>
                        {t('frontpage.step.title.4')}
                      </a>
                    ) : (
                        t('frontpage.step.title.4')
                      )}
                  </Button>
                )}
                {showRegister && (
                  <span className={styles.regClose}>{t('course.registration.closed')}</span>
                )}
                <Modal
                  visible={cancellationModal}
                  title="Cancellation"
                  onCancel={() => {
                    setCancellationModal(false);
                  }}
                  onOk={() => {
                    setCancellationModal(false);
                  }}
                  bodyStyle={{ padding: '32px 24px' }}
                >
                  <InlineEditor
                    label="cancellation"
                    type="textarea"
                    value={course.cancellation}
                    hasTranslations
                  >
                    {course.cancellation}
                  </InlineEditor>
                </Modal>
                {(!!course.cancellation || editMode) && (
                  <span
                    onClick={() => {
                      if (!editMode) {
                        Modal.info({
                          title: t('course.details.content.cancellation'),
                          content: <p>{course.cancellation}</p>,
                        });
                      } else {
                        setCancellationModal(true);
                      }
                    }}
                    className={styles.cancellation}
                  >
                    {t('course.details.content.cancellation')}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
        {/* ----------Rating---------- */}
        {!editMode && !search && (
          <div className={styles.footerCardInfo}>
            <Rating rating={rating} favourites={favourites || '0'} />
            <div className={styles.currentPeriodPos}>{currentPeriod}</div>
          </div>
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  type: PropTypes.string,
  canEdit: PropTypes.bool,
  course: PropTypes.object,
  schedules: PropTypes.array,
  setRegister: PropTypes.func,
  showMonthlyPrice: PropTypes.bool,
  selectedSchedule: PropTypes.object,
  setSelectedSchedule: PropTypes.func,
  search: PropTypes.bool,
};

Header.defaultProps = {
  type: 'header',
  course: {},
  schedules: [],
  canEdit: false,
  setRegister: () => { },
  selectedSchedule: null,
  showMonthlyPrice: false,
  setSelectedSchedule: () => { },
  search: false,
};
// so working
export default React.memo(Header);
